/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/jsx-fragments */
import React from "react";
import PropTypes from "prop-types";

import SidebarUtils from "../../lib/sidebarUtils";
import { OrionContext } from "../orionContext/orionContext";
import { debounce, isMobileBrowser, hasProperty } from "../../lib/utils";
import "./sidebar.scss";
import "./msp-provider.scss";

const IS_MOBILE_BROWSER = isMobileBrowser();

class Sidebar extends React.Component {
  constructor(props, context) {
    super(props, context);
    const isLowerScreenWidth = this.isLowerScreenWidth();

    this.state = {
      isCollapsed:
        props.collapsedByDefault || IS_MOBILE_BROWSER || isLowerScreenWidth,
      isHoverable:
        props.collapsedByDefault || IS_MOBILE_BROWSER || isLowerScreenWidth,
      isTabletView: IS_MOBILE_BROWSER || isLowerScreenWidth
    };

    this.innerRef = React.createRef();
    this.sidebarUtils = new SidebarUtils(context);
    this.onWindowResize = debounce(this.onWindowResize, 100);
  }

  componentDidMount() {
    const sidebar = this.innerRef.current;

    if (sidebar) {
      sidebar.addEventListener("mouseover", this.onSidebarMouseOver);
      sidebar.addEventListener("mouseout", this.onSidebarMouseOut);
    }

    if (!IS_MOBILE_BROWSER) {
      window.addEventListener("resize", this.onWindowResize);
    }
  }

  componentWillUnmount() {
    const sidebar = this.innerRef.current;

    if (sidebar) {
      sidebar.removeEventListener("mouseover", this.onSidebarMouseOver);
      sidebar.removeEventListener("mouseout", this.onSidebarMouseOut);
    }

    if (!IS_MOBILE_BROWSER) {
      window.removeEventListener("resize", this.onWindowResize);
    }
  }

  onWindowResize = () => {
    const { isTabletView } = this.state;
    const isTabletViewCur = this.isLowerScreenWidth();

    if (isTabletViewCur !== isTabletView) {
      this.setState({
        isTabletView: !isTabletView,
        isCollapsed: !isTabletView,
        isHoverable: !isTabletView
      });
    }
  };

  onSidebarMouseOver = () => {
    if (this.innerRef.current.classList.contains("oui-sidebar-hoverable")) {
      this.innerRef.current.classList.add("oui-is-hovering");
    }
  };

  onSidebarMouseOut = () => {
    this.innerRef.current.classList.remove("oui-is-hovering");
  };

  isLowerScreenWidth = () => {
    return window.innerWidth < 992;
  };

  toggle = () => {
    const { isCollapsed } = this.state;
    const { onExpand, onCollapse } = this.props;

    if (isCollapsed) {
      this.setState(
        {
          isCollapsed: false,
          isHoverable: false
        },
        () => setTimeout(onExpand, 300)
      );
    } else {
      this.setState(
        {
          isCollapsed: true
        },
        () => {
          setTimeout(() => {
            this.setState(
              {
                isHoverable: true
              },
              onCollapse
            );
          }, 500);
        }
      );
    }
  };

  render() {
    const { isCollapsed, isHoverable, isTabletView } = this.state;
    return (
      <React.Fragment>
        {hasProperty(this.props, "float")
          ? this.sidebarUtils.renderFloatingSidebar(this.props)
          : this.sidebarUtils.renderStaticSidebar(
              this.props,
              isCollapsed,
              isHoverable,
              this.toggle,
              isTabletView,
              this.innerRef
            )}
      </React.Fragment>
    );
  }
}

Sidebar.contextType = OrionContext;

Sidebar.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      component: PropTypes.func,
      divider: PropTypes.bool,
      className: PropTypes.string
    })
  ).isRequired,
  float: PropTypes.shape({
    direction: PropTypes.oneOf(["left", "right"]),
    visible: PropTypes.bool
  }),
  site: PropTypes.shape({
    name: PropTypes.string,
    icon: PropTypes.shape({
      src: PropTypes.string,
      size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
      width: PropTypes.string,
      height: PropTypes.string,
      classes: PropTypes.string,
      onClick: PropTypes.func,
      className: PropTypes.string
    })
  }),
  collapsible: PropTypes.bool,
  collapsedByDefault: PropTypes.bool,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
  className: PropTypes.string,
  honourGlobalFooter: PropTypes.bool,
  honourPageAlert: PropTypes.bool,
  sidebarName: PropTypes.string,
  footerLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"]),
      renderComponent: PropTypes.func
    })
  ),
  dataTestId: PropTypes.string
};

Sidebar.defaultProps = {
  honourGlobalFooter: false,
  honourPageAlert: false,
  // eslint-disable-next-line react/default-props-match-prop-types
  sections: [],
  collapsible: false,
  collapsedByDefault: false,
  onCollapse: () => {},
  onExpand: () => {},
  sidebarName: null,
  footerLinks: [],
  dataTestId: "orion-sidebar"
};

Sidebar.displayName = "OrionSidebar";

export default Sidebar;
